import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, Download, Eye, X, Award, Check, MessageCircle, Mail, Trash2 } from 'lucide-react';
import StarRating from '../StarRating';
import { COUNTRIES_DATA, COUNTRIES, PROFESSIONS } from '../../data/locations';
import { Pagination } from './Pagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 20;

const STATUS_OPTIONS = ['All', 'New', 'Reviewed', 'Shortlisted', 'Contacted', 'Archived'];
const GENDER_OPTIONS = ['All', 'Male', 'Female'];
const RATING_OPTIONS = [
  { value: 0, label: 'All Ratings' },
  { value: 1, label: '1 Star' },
  { value: 2, label: '2 Stars' },
  { value: 3, label: '3 Stars' },
  { value: 4, label: '4 Stars' },
  { value: 5, label: '5 Stars' },
];

const FALLBACK_TEMPLATES = [
  { id: 'shortlisted', label: 'Shortlisted', message: 'Hello {name}, your profile ({id}) has been shortlisted by BuildDirect Careers for upcoming opportunities. We will be in touch soon.' },
  { id: 'request_photo', label: 'Request Photo', message: 'Hello {name}, thank you for submitting your profile ({id}). Kindly send us your passport photograph to complete your application.' },
  { id: 'request_docs', label: 'Request Documents', message: 'Hello {name}, we require additional documents for your profile ({id}). Please send your updated CV and any relevant certifications.' },
  { id: 'interview', label: 'Interview Invite', message: 'Hello {name}, congratulations! We would like to invite you for an interview regarding your profile ({id}). Please confirm your availability.' },
  { id: 'deployment', label: 'Deployment Notice', message: 'Hello {name}, you have been selected for deployment on a project. Please standby for further instructions regarding your profile ({id}).' },
  { id: 'general', label: 'General Message', message: 'Hello {name}, this is BuildDirect Careers regarding your profile ({id}). ' },
];

const formatPhone = (phone) => (phone || '').replace(/[^0-9+]/g, '').replace(/^0/, '234').replace(/^\+/, '');

const CandidatesTab = () => {
  const [profiles, setProfiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [detailProfile, setDetailProfile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    profession: 'All',
    status: 'All',
    state: 'All',
    country: 'All',
    gender: 'All',
    rating: 0
  });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProfiles, setTotalProfiles] = useState(0);
  const [showWhatsAppMenu, setShowWhatsAppMenu] = useState(false);
  const [showBulkWhatsAppMenu, setShowBulkWhatsAppMenu] = useState(false);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/templates`);
      setWhatsappTemplates(res.data.templates || []);
    } catch { /* fall back to defaults */ }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const params = { limit: PAGE_SIZE, skip: (currentPage - 1) * PAGE_SIZE, ...filters };
      if (searchTerm) params.search = searchTerm;
      Object.keys(params).forEach(key => {
        if (params[key] === 'All' || params[key] === 0) delete params[key];
      });

      const [profilesRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/profiles`, { params }),
        axios.get(`${API_URL}/api/admin/stats`)
      ]);
      setProfiles(profilesRes.data.profiles || []);
      setTotalProfiles(profilesRes.data.total || 0);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, currentPage]);

  useEffect(() => { fetchData(); fetchTemplates(); }, [fetchData, fetchTemplates]);
  useEffect(() => { setCurrentPage(1); }, [filters]);

  const handleSearch = () => {
    setCurrentPage(1);
    setLoading(true);
    fetchData();
  };

  const handleViewProfile = async (profile) => {
    setSelectedProfile(profile);
    try {
      const res = await axios.get(`${API_URL}/api/admin/profiles/${profile.unique_id}`);
      setDetailProfile(res.data);
    } catch {
      setDetailProfile(profile);
    }
  };

  const handleUpdateStatus = async (uniqueId, status, notes) => {
    try {
      const formData = new FormData();
      formData.append('status', status);
      if (notes !== undefined) formData.append('admin_notes', notes || '');
      await axios.patch(`${API_URL}/api/admin/profiles/${uniqueId}`, formData);
      fetchData();
      if (detailProfile && detailProfile.unique_id === uniqueId) {
        setDetailProfile({ ...detailProfile, status, admin_notes: notes || detailProfile.admin_notes });
      }
    } catch (error) {
      console.error('Update error:', error);
    }
  };

  const handleRating = async (uniqueId, rating) => {
    try {
      await axios.put(`${API_URL}/api/admin/profiles/${uniqueId}/rating`, { rating });
      setProfiles(prev => prev.map(p => p.unique_id === uniqueId ? { ...p, rating } : p));
      if (detailProfile && detailProfile.unique_id === uniqueId) {
        setDetailProfile({ ...detailProfile, rating });
      }
    } catch (error) {
      console.error('Rating error:', error);
    }
  };

  const handleToggleRecommended = async (uniqueId) => {
    try {
      const res = await axios.put(`${API_URL}/api/admin/profiles/${uniqueId}/recommend`);
      const newVal = res.data.recommended;
      setProfiles(prev => prev.map(p => p.unique_id === uniqueId ? { ...p, recommended: newVal } : p));
      if (detailProfile && detailProfile.unique_id === uniqueId) {
        setDetailProfile({ ...detailProfile, recommended: newVal });
      }
      fetchData();
    } catch (error) {
      console.error('Recommend error:', error);
    }
  };

  const sendWhatsAppTemplate = (template, profile) => {
    const message = template.message
      .replace('{name}', profile.full_name)
      .replace('{id}', profile.unique_id);
    window.open(`https://wa.me/${formatPhone(profile.phone)}?text=${encodeURIComponent(message)}`, '_blank');
    setShowWhatsAppMenu(false);
  };

  const handleDeleteProfile = async (uniqueId) => {
    if (!window.confirm('Are you sure you want to permanently delete this profile?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/profiles/${uniqueId}`);
      fetchData();
      setSelectedProfile(null);
      setDetailProfile(null);
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(uniqueId);
        return next;
      });
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete profile.');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'professionals.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const toggleSelect = (uniqueId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(uniqueId)) next.delete(uniqueId);
      else next.add(uniqueId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === profiles.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(profiles.map(p => p.unique_id)));
    }
  };

  const handleBulkWhatsAppTemplate = async (template) => {
    if (selectedIds.size === 0) return;
    setShowBulkWhatsAppMenu(false);
    try {
      const res = await axios.post(`${API_URL}/api/admin/bulk-share`, {
        unique_ids: Array.from(selectedIds),
        share_type: 'whatsapp'
      });
      res.data.profiles.forEach((p, i) => {
        setTimeout(() => {
          const message = template.message
            .replace('{name}', p.full_name)
            .replace('{id}', p.unique_id);
          window.open(`https://wa.me/${formatPhone(p.phone)}?text=${encodeURIComponent(message)}`, '_blank');
        }, i * 800);
      });
    } catch (error) {
      console.error('Bulk WhatsApp error:', error);
    }
  };

  const handleBulkEmail = async () => {
    if (selectedIds.size === 0) return;
    try {
      const res = await axios.post(`${API_URL}/api/admin/bulk-share`, {
        unique_ids: Array.from(selectedIds),
        share_type: 'email'
      });
      const candidateList = res.data.profiles;
      const subject = encodeURIComponent(`BuildDirect Careers - ${candidateList.length} Candidate Profile(s)`);
      let body = `BuildDirect Careers - Candidate Profiles\n${'='.repeat(45)}\n\n`;
      candidateList.forEach((p, i) => {
        body += `${i + 1}. ${p.full_name}\n   ID: ${p.unique_id}\n   Profession: ${p.profession}\n   Experience: ${p.experience}\n   State: ${p.state}\n   Rating: ${'★'.repeat(p.rating || 0)}${'☆'.repeat(5 - (p.rating || 0))}\n`;
        if (p.phone) body += `   Phone: ${p.phone}\n`;
        if (p.email) body += `   Email: ${p.email}\n`;
        body += `\n`;
      });
      window.open(`mailto:?subject=${subject}&body=${encodeURIComponent(body)}`, '_self');
    } catch (error) {
      console.error('Email share error:', error);
    }
  };

  const showBulkBar = selectedIds.size > 0;
  const templates = whatsappTemplates.length > 0 ? whatsappTemplates : FALLBACK_TEMPLATES;

  if (loading && !stats) {
    return <div className="text-center py-12 text-slate-400 text-sm">Loading candidates...</div>;
  }

  return (
    <div>
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 md:grid-cols-7 gap-3 mb-6">
          {[
            { label: 'Total', value: stats.total, color: 'text-slate-100' },
            { label: 'New', value: stats.new, color: 'text-blue-600' },
            { label: 'Reviewed', value: stats.reviewed, color: 'text-yellow-600' },
            { label: 'Shortlisted', value: stats.shortlisted, color: 'text-green-600' },
            { label: 'Contacted', value: stats.contacted, color: 'text-purple-600' },
            { label: 'Archived', value: stats.archived, color: 'text-slate-400' },
            { label: 'Recommended', value: stats.recommended || 0, color: 'text-amber-500' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-[#0E1525] border border-slate-800 p-4 rounded" data-testid={`stat-${label.toLowerCase()}`}>
              <div className={`text-2xl font-bold ${color}`}>{value}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mt-1">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Search & Filters */}
      <div className="bg-[#0E1525] border border-slate-800 p-5 mb-6 rounded space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, email, or unique ID..."
            data-testid="admin-search-input"
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="flex-1 border border-slate-700 rounded h-10 px-4 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button onClick={handleSearch} data-testid="admin-search-button"
            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 text-xs uppercase tracking-wider font-semibold rounded transition-colors flex items-center gap-2">
            <Search className="h-4 w-4" /> Search
          </button>
          <button onClick={handleExport} data-testid="export-csv-button"
            className="border border-slate-700 hover:border-slate-500 text-slate-300 px-5 py-2 text-xs uppercase tracking-wider font-semibold rounded transition-colors flex items-center gap-2">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <FilterSelect label="Country" value={filters.country} testId="filter-country"
            onChange={(v) => setFilters({ ...filters, country: v, state: 'All' })}
            options={[{ v: 'All', label: 'All Countries' }, ...COUNTRIES.map(c => ({ v: c, label: c }))]} />
          <FilterSelect label="State / Region" value={filters.state} testId="filter-state"
            onChange={(v) => setFilters({ ...filters, state: v })}
            disabled={filters.country === 'All'}
            options={[
              { v: 'All', label: filters.country === 'All' ? 'Select country first' : 'All States' },
              ...(filters.country !== 'All' ? (COUNTRIES_DATA[filters.country] || []).map(s => ({ v: s, label: s })) : [])
            ]} />
          <FilterSelect label="Profession" value={filters.profession} testId="filter-profession"
            onChange={(v) => setFilters({ ...filters, profession: v })}
            options={[{ v: 'All', label: 'All Professions' }, ...PROFESSIONS.map(p => ({ v: p, label: p }))]} />
          <FilterSelect label="Gender" value={filters.gender} testId="filter-gender"
            onChange={(v) => setFilters({ ...filters, gender: v })}
            options={GENDER_OPTIONS.map(g => ({ v: g, label: g }))} />
          <FilterSelect label="Status" value={filters.status} testId="filter-status"
            onChange={(v) => setFilters({ ...filters, status: v })}
            options={STATUS_OPTIONS.map(s => ({ v: s, label: s }))} />
          <FilterSelect label="Rating" value={filters.rating} testId="filter-rating"
            onChange={(v) => setFilters({ ...filters, rating: parseInt(v) })}
            options={RATING_OPTIONS.map(r => ({ v: r.value, label: r.label }))} />
        </div>
      </div>

      {/* Bulk Action Bar */}
      {showBulkBar && (
        <div className="bg-blue-600 text-white px-5 py-3 mb-4 rounded flex items-center justify-between" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selectedIds.size} candidate{selectedIds.size > 1 ? 's' : ''} selected</span>
          <div className="flex items-center gap-3">
            <div className="relative">
              <button onClick={() => setShowBulkWhatsAppMenu(!showBulkWhatsAppMenu)} data-testid="bulk-whatsapp-button"
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded text-xs uppercase tracking-wider font-semibold transition-colors">
                <MessageCircle className="h-4 w-4" /> WhatsApp All
              </button>
              {showBulkWhatsAppMenu && (
                <div className="absolute right-0 bottom-full mb-2 w-60 bg-[#0E1525] border border-slate-800 rounded-lg shadow-lg z-50" data-testid="bulk-whatsapp-template-menu">
                  <div className="p-2 border-b border-slate-800/60">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Select Message for All</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">Opens 1 WhatsApp tab per candidate</p>
                  </div>
                  {templates.map((tpl) => (
                    <button key={tpl.template_id || tpl.id} onClick={() => handleBulkWhatsAppTemplate(tpl)}
                      data-testid={`bulk-wa-${tpl.template_id || tpl.id}`}
                      className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-green-900/30 hover:text-green-300 transition-colors border-b border-slate-800/40 last:border-0">
                      {tpl.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={handleBulkEmail} data-testid="bulk-email-button"
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded text-xs uppercase tracking-wider font-semibold transition-colors">
              <Mail className="h-4 w-4" /> Email
            </button>
            <button onClick={() => { setSelectedIds(new Set()); setShowBulkWhatsAppMenu(false); }} data-testid="clear-selection-button"
              className="text-white/80 hover:text-white ml-2">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Profiles Table */}
      <div className="bg-[#0E1525] border border-slate-800 rounded overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0B0F1A] border-b border-slate-800">
              <tr>
                <th className="w-10 px-3 py-3">
                  <input type="checkbox" checked={profiles.length > 0 && selectedIds.size === profiles.length} onChange={toggleSelectAll}
                    data-testid="select-all-checkbox" className="h-4 w-4 rounded border-slate-700 text-blue-600 focus:ring-blue-500 cursor-pointer" />
                </th>
                {['ID', 'Photo', 'Name', 'Profession', 'Country', 'State / Region', 'Address', 'Gender', 'Age'].map(h => (
                  <th key={h} className="text-left px-3 py-3 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{h}</th>
                ))}
                <th className="text-center px-3 py-3 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Docs</th>
                <th className="text-left px-3 py-3 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Rating</th>
                <th className="text-center px-3 py-3 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Rec.</th>
                <th className="text-left px-3 py-3 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Status</th>
                <th className="text-left px-3 py-3 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Submitted</th>
                <th className="text-left px-3 py-3 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {profiles.length === 0 ? (
                <tr><td colSpan="15" className="text-center py-12 text-slate-400 text-sm">No profiles found</td></tr>
              ) : (
                profiles.map((profile) => (
                  <tr key={profile.unique_id} className={`border-b border-slate-800/60 hover:bg-[#0B0F1A] transition-colors ${selectedIds.has(profile.unique_id) ? 'bg-blue-900/20' : ''}`}>
                    <td className="px-3 py-3">
                      <input type="checkbox" checked={selectedIds.has(profile.unique_id)} onChange={() => toggleSelect(profile.unique_id)}
                        data-testid={`select-${profile.unique_id}`} className="h-4 w-4 rounded border-slate-700 text-blue-600 focus:ring-blue-500 cursor-pointer" />
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-400 font-mono" data-testid={`profile-id-${profile.unique_id}`}>{profile.unique_id}</td>
                    <td className="px-3 py-3">
                      <CandidateAvatar profile={profile} />
                    </td>
                    <td className="px-3 py-3 text-sm text-slate-100 font-medium">{profile.full_name}</td>
                    <td className="px-3 py-3 text-sm text-slate-300">{profile.profession}</td>
                    <td className="px-3 py-3 text-xs text-slate-300">{profile.country || '-'}</td>
                    <td className="px-3 py-3 text-xs text-slate-300">{profile.state || '-'}</td>
                    <td className="px-3 py-3 text-xs text-slate-300">{profile.address || '-'}</td>
                    <td className="px-3 py-3 text-xs text-slate-300">{profile.gender || '-'}</td>
                    <td className="px-3 py-3 text-xs text-slate-300">{profile.age || '-'}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-center gap-2" data-testid={`docs-${profile.unique_id}`}>
                        {[
                          { code: 'PH', has: profile.passport_photo_path, label: 'Photo' },
                          { code: 'CV', has: profile.cv_path, label: 'CV' },
                          { code: 'PO', has: profile.portfolio_path, label: 'Portfolio' },
                        ].map(({ code, has, label }) => (
                          <span key={code} title={label} className="flex flex-col items-center">
                            <span className={`text-[9px] font-bold ${has ? 'text-green-600' : 'text-slate-300'}`}>{code}</span>
                            {has ? <Check className="h-3 w-3 text-green-400" /> : <X className="h-3 w-3 text-slate-300" />}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <StarRating rating={profile.rating || 0} onRate={(r) => handleRating(profile.unique_id, r)} data-testid={`rating-${profile.unique_id}`} />
                    </td>
                    <td className="px-3 py-3 text-center">
                      <button onClick={() => handleToggleRecommended(profile.unique_id)}
                        data-testid={`recommend-${profile.unique_id}`}
                        className={`transition-colors ${profile.recommended ? 'text-amber-500' : 'text-slate-300 hover:text-amber-400'}`}
                        title={profile.recommended ? 'Remove recommendation' : 'Recommend'}>
                        <Award className="h-4 w-4" />
                      </button>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded ${
                        profile.status === 'New' ? 'bg-blue-900/40 text-blue-300 border border-blue-500/30' :
                        profile.status === 'Reviewed' ? 'bg-yellow-900/40 text-yellow-300 border border-yellow-500/30' :
                        profile.status === 'Shortlisted' ? 'bg-green-900/40 text-green-300 border border-green-500/30' :
                        profile.status === 'Contacted' ? 'bg-purple-900/40 text-purple-300 border border-purple-500/30' :
                        'bg-slate-800/60 text-slate-300 border border-slate-700'
                      }`}>{profile.status}</span>
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-400" data-testid={`submitted-${profile.unique_id}`}>
                      {profile.created_at ? new Date(profile.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                      <br />
                      <span className="text-slate-400">{profile.created_at ? new Date(profile.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                    </td>
                    <td className="px-3 py-3">
                      <button onClick={() => handleViewProfile(profile)} data-testid={`view-profile-${profile.unique_id}`}
                        className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1 font-medium">
                        <Eye className="h-3.5 w-3.5" /> View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination currentPage={currentPage} totalItems={totalProfiles} pageSize={PAGE_SIZE} onPageChange={setCurrentPage} testIdPrefix="page" />

      {/* Profile Detail Modal */}
      {selectedProfile && detailProfile && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => { setSelectedProfile(null); setDetailProfile(null); setShowWhatsAppMenu(false); }}>
          <div className="bg-[#0E1525] max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#0E1525] border-b border-slate-800 px-6 py-4 flex items-center justify-between rounded-t-lg">
              <div>
                <h2 className="text-lg font-bold text-slate-100" data-testid="modal-profile-name">{detailProfile.full_name}</h2>
                <p className="text-xs text-slate-400 font-mono" data-testid="modal-unique-id">{detailProfile.unique_id}</p>
              </div>
              <button onClick={() => { setSelectedProfile(null); setDetailProfile(null); setShowWhatsAppMenu(false); }}
                data-testid="close-modal-button" className="text-slate-400 hover:text-slate-300 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-800/60">
                <div className="flex items-center gap-4">
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Rating</span>
                  <StarRating rating={detailProfile.rating || 0} onRate={(r) => handleRating(detailProfile.unique_id, r)} />
                </div>
                <button onClick={() => handleToggleRecommended(detailProfile.unique_id)} data-testid="modal-recommend-toggle"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold transition-colors ${
                    detailProfile.recommended ? 'bg-amber-900/40 text-amber-300 border border-amber-500/30' : 'bg-slate-800/60 text-slate-400 border border-slate-700 hover:bg-amber-900/20 hover:text-amber-300'
                  }`}>
                  <Award className="h-3.5 w-3.5" />
                  {detailProfile.recommended ? 'Recommended' : 'Recommend'}
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">Email</div>
                  <div className="text-sm text-slate-200 break-all">{detailProfile.email || 'N/A'}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">Phone</div>
                    <div className="text-sm text-slate-200">{detailProfile.phone || 'N/A'}</div>
                  </div>
                  {detailProfile.phone && (
                    <div className="relative">
                      <button onClick={() => setShowWhatsAppMenu(!showWhatsAppMenu)} data-testid="whatsapp-candidate"
                        className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded text-xs font-semibold transition-colors">
                        <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                      </button>
                      {showWhatsAppMenu && (
                        <div className="absolute right-0 top-full mt-1 w-56 bg-[#0E1525] border border-slate-800 rounded-lg shadow-lg z-50" data-testid="whatsapp-template-menu">
                          <div className="p-2 border-b border-slate-800/60">
                            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Select Message</p>
                          </div>
                          {templates.map((tpl) => (
                            <button key={tpl.template_id || tpl.id} onClick={() => sendWhatsAppTemplate(tpl, detailProfile)}
                              data-testid={`wa-template-${tpl.template_id || tpl.id}`}
                              className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-green-900/30 hover:text-green-300 transition-colors border-b border-slate-800/40 last:border-0">
                              {tpl.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { label: 'Gender', value: detailProfile.gender },
                  { label: 'Date of Birth', value: detailProfile.date_of_birth },
                  { label: 'Age', value: detailProfile.age },
                  { label: 'Country', value: detailProfile.country },
                  { label: 'State / Region', value: detailProfile.state },
                  { label: 'Address', value: detailProfile.address },
                  { label: 'Profession', value: detailProfile.profession },
                  { label: 'Experience', value: detailProfile.experience },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">{label}</div>
                    <div className="text-sm text-slate-200">{value || 'N/A'}</div>
                  </div>
                ))}
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">Skills</div>
                <div className="text-sm text-slate-200">{detailProfile.skills || 'N/A'}</div>
              </div>

              {detailProfile.summary && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">Summary</div>
                  <div className="text-sm text-slate-200">{detailProfile.summary}</div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Photo', path: detailProfile.passport_photo_path, testId: 'download-passport' },
                  { label: 'CV', path: detailProfile.cv_path, testId: 'download-cv' },
                  { label: 'Portfolio', path: detailProfile.portfolio_path, testId: 'download-portfolio' },
                  { label: 'ID', path: detailProfile.id_path, testId: 'download-id' },
                ].filter(f => f.path).map(({ label, path, testId }) => (
                  <a key={label} href={`${API_URL}/api/files/${path}`} target="_blank" rel="noopener noreferrer" data-testid={testId}
                    className="border border-slate-800 rounded p-3 text-center hover:bg-[#0B0F1A] transition-colors block">
                    <Download className="h-4 w-4 mx-auto mb-1 text-blue-600" />
                    <span className="text-xs text-blue-600 font-medium">{label}</span>
                  </a>
                ))}
              </div>

              <div className="border-t border-slate-800 pt-5">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-3">Status</div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {STATUS_OPTIONS.filter(s => s !== 'All').map(status => (
                    <button key={status} onClick={() => handleUpdateStatus(detailProfile.unique_id, status, detailProfile.admin_notes)}
                      data-testid={`status-${status.toLowerCase()}`}
                      className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
                        detailProfile.status === status ? 'bg-blue-600 text-white' : 'bg-slate-800/60 text-slate-300 border border-slate-700 hover:bg-slate-700/60'
                      }`}>
                      {status}
                    </button>
                  ))}
                </div>

                <div className="mb-4">
                  <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1 block">Admin Notes</label>
                  <textarea value={detailProfile.admin_notes || ''} onChange={(e) => setDetailProfile({ ...detailProfile, admin_notes: e.target.value })}
                    rows={3} data-testid="admin-notes-textarea"
                    className="w-full border border-slate-700 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  <button onClick={() => handleUpdateStatus(detailProfile.unique_id, detailProfile.status, detailProfile.admin_notes)}
                    data-testid="save-notes-button"
                    className="mt-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 text-xs uppercase tracking-wider font-semibold rounded transition-colors">
                    Save Notes
                  </button>
                </div>

                <div className="border-t border-slate-800 pt-4 mt-4">
                  <button onClick={() => handleDeleteProfile(detailProfile.unique_id)} data-testid="delete-profile-button"
                    className="flex items-center gap-2 text-red-500 hover:text-red-700 text-xs uppercase tracking-wider font-semibold transition-colors">
                    <Trash2 className="h-3.5 w-3.5" /> Delete Profile
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CandidateAvatar = ({ profile }) => {
  const [errored, setErrored] = useState(false);
  const initials = (profile.full_name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();
  const photoUrl = profile.passport_photo_path
    ? `${API_URL}/api/files/${profile.passport_photo_path}`
    : null;
  if (photoUrl && !errored) {
    return (
      <img
        src={photoUrl}
        alt={profile.full_name}
        loading="lazy"
        onError={() => setErrored(true)}
        data-testid={`avatar-${profile.unique_id}`}
        className="h-10 w-10 rounded-full object-cover border border-slate-700 bg-slate-800"
      />
    );
  }
  return (
    <div
      data-testid={`avatar-fallback-${profile.unique_id}`}
      className="h-10 w-10 rounded-full bg-blue-900/40 text-blue-300 border border-blue-500/30 flex items-center justify-center text-[11px] font-bold tracking-wide"
      title={profile.full_name}
    >
      {initials}
    </div>
  );
};

const FilterSelect = ({ label, value, onChange, options, disabled, testId }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1 block">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} disabled={disabled}
      className={`w-full border border-slate-700 rounded h-9 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${disabled ? 'bg-slate-800/60 text-slate-500 cursor-not-allowed' : ''}`}>
      {options.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
  </div>
);

export default CandidatesTab;
